import { ecId, ecLean, record, upper, type JanusReaders, type Row } from '../ability/context';
import { frameCopyOnRead } from '../ability/frame-snapshot';
import { appliedSpawnRows } from '../transport/member-applied-frame';
import { gmStatusAppliedFrame } from '../transport/gm-applied-frame';
import { appliedStatusRows } from '../transport/applied-status-rows';
import type { AppliedFrame, AppliedTouchedRow } from '../transport/applied-frame';
import type { GmBoundaryVerdict } from '../statuses/gm-boundary';
import { statusRows, statusItemId } from '../statuses/expiry';
import { readItemField } from '../compile/item-field-reader';
import { readInventoryField } from '../compile/inventory-raw-readers';
import type { AttackStatusScene } from '../actions/attack-status-projection';
import type { TrapArmPlan } from './arm';
import type { DiscernPlan } from './discern-save';
import type { DisarmBeginPlan } from './disarm-begin';
import type { TrapLifecycle } from './lifecycle';

/** Serialize native trap and key planners' writes with the shared status/resource delta projector.
 * Only changed rows travel; no saved-base fetch or whole-member result is needed. */
export function trapCommitFrame(command: Row, encounter: Row, actor: Row, host: Row, campaign: Row | null,
    read: Pick<JanusReaders, 'definition'>, scene?: AttackStatusScene, inventoryTails?: GmBoundaryVerdict): AppliedFrame {
    const aid = upper(command.actor_id), eid = upper(command.encounter_id), action = String(command.action);
    const verdict: GmBoundaryVerdict = { rows: [], ec: {}, statusesAdded: [], statusesRemoved: [], statusStacks: [], charges: [], playerCharactersChanged: [] };
    if (inventoryTails) Object.assign(verdict, inventoryTails);
    const extra: AppliedTouchedRow[] = [];
    let lifecycle: TrapLifecycle | undefined;
    if (action === 'key') {
        Object.assign(verdict, command.key_boundary as GmBoundaryVerdict);
    } else if (action === 'trap_arm') {
        const plan = command.arm as TrapArmPlan;
        Object.assign(verdict, plan.verdict);
        verdict.statusesAdded = plan.verdict.statusesAdded.map(status => ({ ...status, status_private: status.kind === 'armed' ? 1 : 0 }));
    } else if (action === 'trap_discern') {
        const plan = command.check as DiscernPlan;
        Object.assign(verdict, plan.verdict);
        if (plan.noticed) {
            const sid = upper(plan.noticed.status_id);
            const held = statusRows(host, 'target').find(row => upper(readItemField(row, 'status_id')) === sid);
            const nested = statusRows(held ?? null, 'target').filter(row => statusItemId(row) !== '47667406-1B72-9D60-310B-D8CF6BFE397D');
            extra.push({ table: 'statuses', row_id: sid, partial: true,
                row: { status_id: sid, status_target_id: upper(command.host_inventory_id), status_private: 0, statuses: { ...record(held?.statuses), target: nested } } });
        }
    } else if (action === 'trap_disarm_begin') {
        const plan = command.plan as DisarmBeginPlan;
        if (plan.ap) verdict.ec[aid] = { aps: plan.ap.after };
        lifecycle = { removals: plan.status_retire.map(op => ({ status_id: String(op.id), holder_type: 'inventory', before: op.before as TrapLifecycle['removals'][number]['before'] })), skipped: [] };
    } else if (action === 'trap_disarm_result') {
        const result = record(command.verdict)!;
        Object.assign(verdict, record(result.save));
        lifecycle = result.lifecycle as TrapLifecycle;
    }
    const fire = record(record(command.fire)?.verdict);
    if (fire) {
        for (const key of ['rows', 'statusesAdded', 'statusesRemoved', 'statusStacks', 'charges'] as const)
            verdict[key] = [...verdict[key], ...Array.isArray(fire[key]) ? fire[key] as never[] : []] as never;
        verdict.ec = { ...verdict.ec, ...record(fire.ec) as Record<string, Row> };
        verdict.playerCharactersChanged = [...verdict.playerCharactersChanged, ...Array.isArray(fire.pc) ? fire.pc as GmBoundaryVerdict['playerCharactersChanged'] : []];
        verdict.spawnRows = fire.spawnRows as GmBoundaryVerdict['spawnRows'];
        lifecycle = fire.lifecycle as TrapLifecycle;
    }
    const maintenance = record(command.maintenance) ?? record(record(command.fire)?.maintenance);
    if (maintenance) verdict.rows = [...verdict.rows, ...maintenance.rows as GmBoundaryVerdict['rows']];
    if (maintenance) lifecycle = { removals: [...(lifecycle?.removals ?? []), ...maintenance.removals as TrapLifecycle['removals']], skipped: lifecycle?.skipped ?? [] };
    const roster = new Map<string, Row>();
    for (const row of Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) {
        const member = record(row), id = ecId(member); if (member && id) roster.set(id, member);
    }
    roster.set(aid, actor);
    const holders: Row[] = [encounter, host, ...roster.values()];
    const status = (id: string): Row | undefined => {
        for (const holder of holders) {
            const found = statusRows(holder, 'target').find(row => upper(readItemField(row, 'status_id')) === upper(id));
            if (found) return found;
        }
        return undefined;
    };
    verdict.statusesRemoved = [...verdict.statusesRemoved];
    for (const removal of lifecycle?.removals ?? []) {
        const held = status(removal.status_id);
        verdict.statusesRemoved.push({ status_id: removal.status_id, status_item_id: held ? statusItemId(held) : '', target: removal.before.status_target_id });
    }
    const members = frameCopyOnRead(encounter, roster, { catalogs: 'share' });
    members.encounter.encounter_creatures_arr = [...members.members.values()];
    const rev = Number(['trap_arm', 'trap_discern', 'trap_disarm_begin'].includes(action) ? command.expected_rev ?? ecLean(actor, 'movement_rev') ?? 0 : ecLean(actor, 'movement_rev') ?? 0) + (['trap_arm', 'trap_discern'].includes(action)
        || action === 'trap_disarm_begin' && (command.plan as DisarmBeginPlan).ap !== null || verdict.ec[aid] ? 1 : 0);
    const frame = gmStatusAppliedFrame({ operation_id: String(command.operation_id), encounter_id: eid, actor_id: aid, rev, member_revs: {} },
        verdict, members.encounter, campaign ?? { campaigns: {} }, read.definition, scene) as AppliedFrame;
    for (const id of Object.keys(frame.member_revs)) {
        const member = roster.get(id)!;
        (frame.member_revs as Record<string, number>)[id] = id === aid ? rev : Number(ecLean(member, 'movement_rev') ?? 0) + (verdict.ec[id] ? 1 : 0);
    }
    const spawnWrites = new Set(appliedSpawnRows(verdict.spawnRows).map(row => row.table + ':' + upper(row.row_id)));
    const changedStatuses = new Set([...verdict.statusesAdded.map(row => upper(row.status_id)), ...verdict.statusStacks.map(row => upper(row.status_id))]);
    // memberAppliedFrame keeps legacy full-row support. This lane emits only submitted columns.
    frame.rows = frame.rows.flatMap(row => {
        if (spawnWrites.has(row.table + ':' + upper(row.row_id))) return [row];
        if (row.table === 'statuses') return changedStatuses.has(upper(row.row_id)) ? [row] : [];
        if (row.table === 'encounter_creatures') {
            const id = upper(row.row_id), changes = verdict.ec[id];
            return changes || id === aid ? [{ table: row.table, row_id: id, partial: true as const,
                row: { ...changes, encounter_creature_id: id, encounter_id: eid, movement_rev: frame.member_revs[id] } }] : [];
        }
        if (row.table === 'player_characters') {
            const changes = verdict.playerCharactersChanged.filter(change => upper(change.item_id) === upper(row.row_id));
            return changes.length ? [{ ...row, partial: true as const, row: { item_id: row.row_id, ...Object.fromEntries(changes.map(change => [change.column, change.after])) } }] : [];
        }
        if (row.table === 'charges') return verdict.charges.some(change => upper(change.holder) === upper(row.row.charge_holder) && upper(change.item_id) === upper(row.row.item_id)) ? [row] : [];
        return row.table === 'inventory' && verdict.spawnRows?.writes.some(write => upper(readInventoryField({ inventory: write.row }, 'inventory_id')) === upper(row.row_id)) ? [row] : [];
    });
    const additions = appliedStatusRows(verdict.statusesAdded, campaign ?? { campaigns: {} }, verdict.statusStacks, holders);
    const known = new Set(frame.rows.map(row => row.table + ':' + row.row_id));
    frame.rows = [...frame.rows, ...additions.filter(row => !known.has(row.table + ':' + row.row_id)), ...extra];
    const noticed = action === 'trap_discern' ? (command.check as DiscernPlan).noticed : null;
    if (noticed) {
        const held = status(noticed.status_id);
        for (const hidden of statusRows(held ?? null, 'target')) if (statusItemId(hidden) === '47667406-1B72-9D60-310B-D8CF6BFE397D')
            (frame.deletes as { table: string; row_id: string }[]).push({ table: 'statuses', row_id: upper(readItemField(hidden, 'status_id')) });
    }
    // Existing members already hold their authored catalog; only newly added status definitions cross the wire.
    const { member_catalogs: _heldCatalogs, ...inputs } = frame.inputs ?? {};
    frame.inputs = { ...inputs, trap_logs: verdict.rows };
    return frame;
}
